import { useCallback, useEffect, useState, RefObject } from "react";

interface UseScrollDetectionOptions {
  threshold?: number;
  isOpen?: boolean;
  children?: React.ReactNode;
}

interface UseScrollDetectionReturn {
  showScrollToBottom: boolean;
  scrollToBottom: () => void;
  handleScroll: () => void;
}

const useScrollDetection = (
  scrollContainerRef: RefObject<HTMLElement>,
  options: UseScrollDetectionOptions = {},
): UseScrollDetectionReturn => {
  const { threshold = 10, isOpen = true, children } = options;
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrolledToBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < threshold;
    const hasScrollableContent = scrollHeight > clientHeight;

    setShowScrollToBottom(hasScrollableContent && !scrolledToBottom);
  }, [scrollContainerRef, threshold]);

  const scrollToBottom = useCallback(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    scrollContainerRef.current.scrollTo({
      top: scrollContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [scrollContainerRef]);

  // Check scroll position when content changes or sidebar opens
  useEffect(() => {
    if (isOpen && scrollContainerRef.current) {
      // Initial check after a small delay
      const timer = setTimeout(() => {
        checkScrollPosition();
      }, 100);

      // Set up MutationObserver to watch for content changes
      const observer = new MutationObserver(() => {
        // Debounce the check to avoid excessive calls
        setTimeout(() => {
          checkScrollPosition();
        }, 50);
      });

      // Observe the scroll container for changes
      observer.observe(scrollContainerRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });

      // Also check on window resize
      const handleResize = () => {
        checkScrollPosition();
      };
      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(timer);
        observer.disconnect();
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen, checkScrollPosition, scrollContainerRef, children]);

  return {
    showScrollToBottom,
    scrollToBottom,
    handleScroll: checkScrollPosition,
  };
};

export default useScrollDetection;
